// #468 C1: THE single source of truth for the three box-fill decisions -
//   (a) can two things share a box  (canShareBox / boxMixGroupLive)
//   (b) is a box full / how full     (computeBoxFill / lineDenom)
//   (c) what fills a box             (fitsInBox; the endpoint resolves mates)
//
// Every surface (the canonical packer, box-fill, box-candidates, the "Fill this
// box" carousel + its endpoint, the checkout gate, the invoice packer) calls
// THIS module. The C2 coverage guard fails CI if any other file makes one of
// these decisions or reads the stored packing fields directly.
//
// LIVE, never the stale stamp: every decision resolves the variety by SLUG from
// a live lookup - the caller's explicit Map (server / endpoint), else the client
// variety cache (primed fresh on cart mount). The `line.mixGroup` /
// line.unitsPerBox stamps are a last-resort fallback only (offline / a line the
// lookup does not know), never the primary source. The static seed is NEVER a
// silent source (it is inert on live per E-025).

import type { BoxCapacity, BoxCode, Variety } from "@/lib/catalog/types";
import {
  expectedMixGroup,
  expectedSaleMode,
} from "@/lib/content-lint/packing-matrix-rules";
import { boxCapacityForLength } from "@/lib/catalog/pricing";
import { getCartVarietyLookup } from "./variety-cache";
import { getVarietyBySlug as staticVarietyBySlug } from "@/lib/catalog/static-varieties";
import type { CartBox } from "./types";

export type VarietyLookup = Pick<Map<string, Variety>, "get">;

// The live resolver, by priority:
//   1. the caller's explicit lookup (server packer / endpoint) - authoritative;
//   2. else, ON THE CLIENT ONLY, the variety cache (primed fresh on cart mount);
//   3. else (server, no explicit lookup) undefined -> the caller uses the line
//      stamp, which is live-at-materialize on the server (built from the server's
//      own live lookup). The static seed is NEVER a silent source.
function resolve(slug: string, lookup?: VarietyLookup): Variety | undefined {
  if (lookup) return lookup.get(slug);
  if (typeof window !== "undefined") return getCartVarietyLookup().get(slug);
  return undefined;
}

// ---------------------------------------------------------------------------
// Variety-level primitives (moved here from the old box-units.ts).
// ---------------------------------------------------------------------------

// The mix group that governs who may share a box. box_only never mixes -> null.
// Falls back to the matrix default (sub-category) for an un-synced row.
export function mixGroupOf(v: Variety): number | null {
  if (v.saleMode === "box_only") return null;
  return v.mixGroup !== undefined ? v.mixGroup : expectedMixGroup(v);
}

// #63 box_stem: the effective sale mode - stored value wins, else the locked
// matrix default (hydrangea -> box_stem), mirroring mixGroupOf. So the grade
// rules below hold even for a row whose saleMode has not been synced yet.
function saleModeOf(v: Variety): string {
  return v.saleMode ?? expectedSaleMode(v);
}

// The unit-count capacity ({EB,QB,HB}) for a variety at a length - bunches for
// box_bunch, stems for box_stem. STORED unitsPerBox wins; otherwise derive from
// the variety's OWN boxSizes / stemsPerBunch (the exact pre-#468 target).
export function unitsPerBoxCapFor(
  v: Variety,
  length: number | undefined,
): BoxCapacity | null {
  const upb = v.unitsPerBox;
  if (upb) {
    if (length !== undefined && upb[String(length)]) return upb[String(length)];
    if (upb.any) return upb.any;
    const first = Object.values(upb)[0];
    if (first) return first;
  }
  const box =
    boxCapacityForLength(v, length ?? v.lengths[0] ?? 0) ??
    v.boxSizes.any ??
    null;
  if (!box) return null;
  const spb = v.stemsPerBunch;
  if (spb === null || spb <= 0) return box; // box_stem: units == stems == capacity
  return {
    EB: box.EB === null ? null : Math.floor(box.EB / spb),
    QB: box.QB === null ? null : Math.floor(box.QB / spb),
    HB: box.HB === null ? null : Math.floor(box.HB / spb),
  };
}

// Units that fill one box of `size` for this variety at `length` - the
// fractional denominator (one unit = 1/thisNumber of a box).
export function unitsPerBoxAtSize(
  v: Variety,
  length: number | undefined,
  size: BoxCode,
): number | null {
  const cap = unitsPerBoxCapFor(v, length);
  const n = cap?.[size];
  return typeof n === "number" && n > 0 ? n : null;
}

// (a) Two varieties may share a box iff they resolve to the SAME non-null mix
// group. box_only (null) never shares.
// #63 box_stem: additionally, one box holds ONE grade - so two box_stem
// varieties (hydrangea) share only when they are the SAME grade (Select colors
// mix with Select colors; a Select and a Premium never share). Grade rides on
// the variety (separate SKUs). Non-box_stem pairs are UNCHANGED.
export function canShareBox(a: Variety, b: Variety): boolean {
  const ga = mixGroupOf(a);
  const gb = mixGroupOf(b);
  if (ga === null || ga !== gb) return false;
  if (saleModeOf(a) === "box_stem" && saleModeOf(b) === "box_stem") {
    return (a.grade ?? null) === (b.grade ?? null);
  }
  return true;
}

// #63 box_stem: the loose-stem packing partition key - one box holds ONE grade
// of ONE mix group. Same-grade colors (separate SKUs) share a box; different
// grades never do. Resolved LIVE by slug (grade rides on the variety, not the
// line); the static seed is the offline last resort, same as every other
// resolver here. null when the slug is not a box_stem variety (or box_only).
export function stemShareKey(slug: string, lookup?: VarietyLookup): string | null {
  const v = resolve(slug, lookup) ?? staticVarietyBySlug(slug);
  if (!v || saleModeOf(v) !== "box_stem") return null;
  const g = mixGroupOf(v);
  if (g === null) return null;
  return `${g}#${v.grade ?? ""}`;
}

// ---------------------------------------------------------------------------
// Box-level resolution (LIVE by slug; stamp is last resort).
// ---------------------------------------------------------------------------

// The box's mix group, from its anchor line's LIVE variety (by slug).
export function boxMixGroupLive(
  box: CartBox,
  lookup?: VarietyLookup,
): number | null {
  const first = box.contents[0];
  if (!first) return null;
  const v = resolve(first.slug, lookup);
  if (v) return mixGroupOf(v);
  if (first.mixGroup !== undefined) return first.mixGroup; // live-at-materialize stamp
  const sv = staticVarietyBySlug(first.slug); // last resort: fixtures / unprimed slug
  return sv ? mixGroupOf(sv) : null;
}

// A packable unit (a CartLine or the packer's BucketItem) - the fields the
// box-share denominator needs. Structural so both callers route through here.
export type PackUnit = {
  slug: string;
  length?: number;
  unitsPerBox?: BoxCapacity;
};

// The per-unit box-share denominator for a box of `size`, resolved LIVE (the
// unit's own slug -> live variety), with the stamp as last resort.
export function lineDenom(
  unit: PackUnit,
  size: BoxCode,
  lookup?: VarietyLookup,
): number | null {
  const v = resolve(unit.slug, lookup);
  if (v) {
    const d = unitsPerBoxAtSize(v, unit.length, size);
    if (d && d > 0) return d;
  }
  const stamped = unit.unitsPerBox?.[size]; // live-at-materialize stamp
  if (typeof stamped === "number" && stamped > 0) return stamped;
  const sv = staticVarietyBySlug(unit.slug); // last resort: fixtures / unprimed slug
  if (sv) {
    const d = unitsPerBoxAtSize(sv, unit.length, size);
    if (d && d > 0) return d;
  }
  return null;
}

const EPS = 1e-9;
// #468 Option-B: ladder rung order (smallest -> largest).
const LADDER_ORDER: BoxCode[] = ["EB", "QB", "HB"];

export type BoxFillMode = "bunch" | "stems";
// #468 Option-B: one rung of the fill ladder for a single-denom box - a size the
// variety offers, its bunch target (denom), and how many more bunches complete
// it (0 = already at that size). `best` marks the QB-default "best value" rung.
export type FillLadderRung = {
  size: BoxCode;
  denom: number;
  bunchesNeeded: number;
  best: boolean;
  // #468 fill-communication: `disabled` = the box already holds MORE than this
  // size can (can't down-select to it); `readyNow` = the box's bunch count
  // EXACTLY fills this size (a "complete now" option the client can pick even if
  // it isn't the QB default); `selected` = the box's current target size.
  disabled: boolean;
  readyNow: boolean;
  selected: boolean;
};
export type BoxFill = {
  mode: BoxFillMode;
  spb: number | null;
  stemsUsed: number;
  bunchesUsed: number;
  bunchTarget: number;
  stemsTarget: number;
  bunchesNeeded: number;
  stemsNeeded: number;
  full: boolean;
  fillPct: number;
  totalShare: number;
  homogeneous: boolean;
  // #468 Option-B: the size ladder for a single-denom bunch box. A box is
  // COMPLETE at ANY rung (EB/QB/HB), not only QB - QB is just the default nudge
  // (`bestValueSize`). `completeSize` is the rung it currently satisfies (if
  // full). Empty for box-mode / multi-denom legacy boxes.
  ladder: FillLadderRung[];
  completeSize: BoxCode | null;
  bestValueSize: BoxCode | null;
  // #468 fill-communication: the effective TARGET size the box is completing
  // toward (box.selectedSize, else the packer's box.size). `full` keys off it.
  selectedSize: BoxCode | null;
};

function clampPct(ratio: number): number {
  if (!Number.isFinite(ratio) || ratio <= 0) return 0;
  return Math.min(100, Math.round(ratio * 100));
}

function dominantBunchSpb(box: CartBox): number | null {
  let best: number | null = null;
  let bestStems = -1;
  for (const line of box.contents) {
    if (
      line.unit === "bunch" &&
      line.stemsPerBunch !== null &&
      line.stemsPerBunch > 0 &&
      line.stems > bestStems
    ) {
      best = line.stemsPerBunch;
      bestStems = line.stems;
    }
  }
  return best;
}

// (b) ONE fill computation: fractional box-share. Each bunch is 1/lineDenom of a
// box; full when the lines' shares sum to >= 1.0. Reads unitsPerBox LIVE via
// lineDenom, which itself falls back to the variety's own boxSizes/spb (the
// pre-#468 target) so nothing regresses.
export function computeBoxFill(box: CartBox, lookup?: VarietyLookup): BoxFill {
  const stemsUsed = box.contents.reduce((s, c) => s + c.stems, 0);
  const bunchesUsed = box.contents.reduce(
    (b, c) => b + (c.unit === "bunch" ? c.quantity : 0),
    0,
  );
  const capStems = box.capacity.stems;
  const spb = dominantBunchSpb(box);

  const isBoxMode =
    box.contents.length > 0 && box.contents.every((c) => c.unit === "box");
  if (isBoxMode) {
    const target = capStems > 0 ? capStems : stemsUsed;
    return {
      mode: "stems",
      spb: null,
      stemsUsed,
      bunchesUsed: 0,
      bunchTarget: 0,
      stemsTarget: target,
      bunchesNeeded: 0,
      stemsNeeded: 0,
      full: true,
      fillPct: 100,
      totalShare: 1,
      homogeneous: true,
      ladder: [],
      completeSize: null,
      bestValueSize: null,
      selectedSize: null,
    };
  }

  // #63 box_stem: loose-stem fill. A stem box holds one grade of one mix group;
  // its per-size capacity is the grade's unitsPerBox STEM count (Select EB 20 /
  // QB 40; Premium QB 35; Mini QB 60; HB null -> hidden). It counts STEMS toward
  // that cap and emits the SAME size-ladder shape as a bunch box, so the size
  // chips + fill bar are REUSED (not forked). mode stays "stems"; a POPULATED
  // ladder is what distinguishes a stem-FILL box from a discrete whole-box
  // hydrangea box (empty ladder). Runs only when every line is a stem line, so
  // bunch/box packing is untouched.
  const stemLines = box.contents.filter((c) => c.unit === "stem");
  if (stemLines.length > 0 && stemLines.length === box.contents.length) {
    const stemDenomAt = (size: BoxCode): number => {
      let m = 0;
      for (const l of stemLines) {
        const d = lineDenom(l, size, lookup);
        if (d && d > m) m = d;
      }
      return m;
    };
    const rungs: { size: BoxCode; denom: number }[] = [];
    for (const size of LADDER_ORDER) {
      const d = stemDenomAt(size);
      if (d && d > 0) rungs.push({ size, denom: d }); // null HB is skipped
    }
    if (rungs.length > 0) {
      rungs.sort((a, b) => a.denom - b.denom);
      const qb = rungs.find((r) => r.size === "QB");
      const maxRung = rungs[rungs.length - 1];
      const bestValueSize = (qb ?? maxRung).size;
      const chosenSize = box.contents.find((c) => c.groupBoxSize)?.groupBoxSize;
      const target =
        rungs.find((r) => r.size === chosenSize) ??
        rungs.find((r) => r.size === box.selectedSize) ??
        rungs.find((r) => r.size === box.size) ??
        qb ??
        maxRung;
      const stemsTarget = target.denom;
      const full = stemsUsed >= stemsTarget;
      const ladder: FillLadderRung[] = rungs.map((r) => ({
        size: r.size,
        denom: r.denom,
        // stems (not bunches) needed to complete this size - the field is reused.
        bunchesNeeded: Math.max(0, r.denom - stemsUsed),
        best: r.size === bestValueSize,
        disabled: false,
        readyNow: r.denom === stemsUsed,
        selected: r.size === target.size,
      }));
      return {
        mode: "stems",
        spb: null,
        stemsUsed,
        bunchesUsed: 0,
        bunchTarget: 0,
        stemsTarget,
        bunchesNeeded: 0,
        stemsNeeded: Math.max(0, stemsTarget - stemsUsed),
        full,
        fillPct: stemsTarget > 0 ? clampPct(stemsUsed / stemsTarget) : 100,
        totalShare: stemsTarget > 0 ? stemsUsed / stemsTarget : 0,
        homogeneous: true,
        ladder,
        completeSize: full ? target.size : null,
        bestValueSize,
        selectedSize: target.size,
      };
    }
  }

  let totalShare = 0;
  const denoms = new Set<number>();
  let anyDenom = false;
  let predominantDenom: number | null = null;
  let predominantStems = -1;
  for (const line of box.contents) {
    if (line.unit !== "bunch") continue;
    let denom = lineDenom(line, box.size, lookup);
    // #379 last resort: a line that resolves nowhere but has an spb + a box stem
    // cap uses floor(capStems / spb) - keeps pre-#468 fixtures/legacy exact.
    if (
      (!denom || denom <= 0) &&
      line.stemsPerBunch !== null &&
      line.stemsPerBunch > 0 &&
      capStems > 0
    ) {
      denom = Math.floor(capStems / line.stemsPerBunch);
    }
    if (denom && denom > 0) {
      totalShare += line.quantity / denom;
      denoms.add(denom);
      anyDenom = true;
      if (line.stems > predominantStems) {
        predominantStems = line.stems;
        predominantDenom = denom;
      }
    }
  }

  if (!anyDenom) {
    const full = capStems > 0 && stemsUsed >= capStems;
    return {
      mode: "stems",
      spb: null,
      stemsUsed,
      bunchesUsed,
      bunchTarget: 0,
      stemsTarget: capStems,
      bunchesNeeded: 0,
      stemsNeeded: Math.max(0, capStems - stemsUsed),
      full,
      fillPct: capStems > 0 ? clampPct(stemsUsed / capStems) : 0,
      totalShare: capStems > 0 ? stemsUsed / capStems : 0,
      homogeneous: true,
      ladder: [],
      completeSize: null,
      bestValueSize: null,
      selectedSize: null,
    };
  }

  // #353 DORMANT group-A mix box: carries an effective BUNCH cap (capacity
  // .bunches). Not created under the mixGroup model, but preserved - such a box
  // is full at its bunch cap, clamped by any homogeneous denom.
  const bunchCap = box.capacity.bunches;
  if (bunchCap !== undefined) {
    const derived = denoms.size === 1 ? ([...denoms][0] ?? 0) : 0;
    const t = derived > 0 ? Math.min(bunchCap, derived) : bunchCap;
    const st = spb ? t * spb : capStems;
    return {
      mode: "bunch",
      spb,
      stemsUsed,
      bunchesUsed,
      bunchTarget: t,
      stemsTarget: st,
      bunchesNeeded: Math.max(0, t - bunchesUsed),
      stemsNeeded: Math.max(0, st - stemsUsed),
      full: bunchesUsed >= t,
      fillPct: t > 0 ? clampPct(bunchesUsed / t) : 0,
      totalShare: t > 0 ? bunchesUsed / t : 0,
      homogeneous: true,
      ladder: [],
      completeSize: null,
      bestValueSize: null,
      selectedSize: null,
    };
  }

  const homogeneous = denoms.size <= 1;

  // #468 ROSE LENGTH-MIX (locked Sep 10): roses (category=rose) mix any 50/60/70
  // in ONE box, so a rose box is NOT single-denom - but it still has ONE target
  // per size: the MAX per-length denom across its lines. All-70 QB = 4, a QB that
  // mixes in any 50/60 = 5, HB = 12 (any lengths). Using max() reuses lineDenom
  // (the single source) and matches the packer, so adding a 50/60 to an all-70 QB
  // expands its target 4->5 exactly as the rule states. Non-rose boxes stay
  // single-denom (homogeneous) and are unchanged.
  const isRoseBox =
    box.contents.length > 0 && box.contents.every((c) => c.category === "rose");

  // #468 fill-communication (supersedes the Option-B "complete at ANY size"):
  // a box is COMPLETE at its SELECTED size (box.selectedSize, else the packer's
  // box.size). QB stays the "best value" default nudge, and any smaller size the
  // box EXACTLY fills is surfaced as "ready now" in the ladder - so nobody is
  // forced to a QB, but the fill bar + gate no longer flash "EB complete" while
  // the client is filling toward a QB.
  const domLine = box.contents.find((c) => c.unit === "bunch") ?? null;
  if ((isRoseBox || homogeneous) && domLine) {
    // Rose box: per-size denom = MAX per-length denom across the box's bunch
    // lines. Non-rose (single-denom) box: the dominant line's own per-size denom.
    const denomAt = (size: BoxCode): number => {
      if (!isRoseBox) return lineDenom(domLine, size, lookup) ?? 0;
      let m = 0;
      for (const l of box.contents) {
        if (l.unit !== "bunch") continue;
        const d = lineDenom(l, size, lookup);
        if (d && d > m) m = d;
      }
      return m;
    };
    const rungs: { size: BoxCode; denom: number }[] = [];
    for (const size of LADDER_ORDER) {
      const d = denomAt(size);
      if (d && d > 0) rungs.push({ size, denom: d });
    }
    if (rungs.length > 0) {
      rungs.sort((a, b) => a.denom - b.denom);
      const qb = rungs.find((r) => r.size === "QB");
      const maxRung = rungs[rungs.length - 1];
      const bestValueSize = (qb ?? maxRung).size;
      // #468 SIZE DRIVES PACKING: target = the GROUP's chosen size (groupBoxSize
      // on the lines, the packer input), else the legacy per-box selectedSize,
      // else the packed box.size, else QB / largest. Reading groupBoxSize here
      // makes the fill bar + gate follow the choice immediately, even before a
      // re-pack settles box.size.
      const chosenSize = box.contents.find(
        (c) => c.unit === "bunch" && c.groupBoxSize,
      )?.groupBoxSize;
      const target =
        rungs.find((r) => r.size === chosenSize) ??
        rungs.find((r) => r.size === box.selectedSize) ??
        rungs.find((r) => r.size === box.size) ??
        qb ??
        maxRung;
      const bunchTarget = target.denom;
      // Complete = the box reaches its chosen/selected size (>= guards overfill).
      const full = bunchesUsed >= bunchTarget;
      const ladder: FillLadderRung[] = rungs.map((r) => ({
        size: r.size,
        denom: r.denom,
        bunchesNeeded: Math.max(0, r.denom - bunchesUsed),
        best: r.size === bestValueSize,
        // #468 SIZE DRIVES PACKING: every offered size is selectable now (choosing
        // a smaller size re-packs into more/smaller boxes), so nothing is disabled.
        disabled: false,
        readyNow: r.denom === bunchesUsed, // this size exactly completes now
        selected: r.size === target.size,
      }));
      const stemsTarget = spb ? bunchTarget * spb : capStems;
      // For a rose box the per-unit share sum above is meaningless (two denoms);
      // its true share is bunches / target. Non-rose keeps the single-denom sum.
      const shareOut = isRoseBox
        ? bunchTarget > 0
          ? bunchesUsed / bunchTarget
          : 0
        : totalShare;
      return {
        mode: "bunch",
        spb,
        stemsUsed,
        bunchesUsed,
        bunchTarget,
        stemsTarget,
        bunchesNeeded: Math.max(0, bunchTarget - bunchesUsed),
        stemsNeeded: Math.max(0, stemsTarget - stemsUsed),
        full,
        fillPct: bunchTarget > 0 ? clampPct(bunchesUsed / bunchTarget) : 100,
        totalShare: shareOut,
        homogeneous: true,
        ladder,
        completeSize: full ? target.size : null,
        bestValueSize,
        selectedSize: target.size,
      };
    }
  }

  // Legacy / multi-denom fallback (transient: the Option-B packer only emits
  // single-denom boxes, and canonicalize re-packs any legacy box on next write).
  const full = totalShare >= 1 - EPS;
  const target = homogeneous ? ([...denoms][0] ?? 0) : 0;
  const stemsTarget = homogeneous && spb ? target * spb : capStems;
  const bunchesNeeded = homogeneous
    ? Math.max(0, target - bunchesUsed)
    : Math.max(
        0,
        Math.ceil(Math.max(0, 1 - totalShare) * (predominantDenom ?? 0)),
      );

  return {
    mode: "bunch",
    spb,
    stemsUsed,
    bunchesUsed,
    bunchTarget: target,
    stemsTarget,
    bunchesNeeded,
    stemsNeeded: Math.max(0, stemsTarget - stemsUsed),
    full,
    fillPct: clampPct(totalShare),
    totalShare,
    homogeneous,
    ladder: [],
    completeSize: null,
    bestValueSize: null,
    selectedSize: null,
  };
}

// (c) Can a candidate variety top off THIS box: same mix group (LIVE), and one
// more bunch keeps the box's share <= 1.0. box_only never fits. Bunch varieties
// only (hydrangea by-the-stem is A2).
export function fitsInBox(
  box: CartBox,
  candidate: Variety,
  lookup?: VarietyLookup,
): boolean {
  const first = box.contents[0];
  if (!first) return false;
  const group = mixGroupOf(candidate);
  if (group === null) return false;
  if (boxMixGroupLive(box, lookup) !== group) return false;

  // #63 Phase 2: box_stem (hydrangea) "Fill this box". A loose-stem candidate
  // tops off a STEM-FILL box of the SAME grade only (one grade per box; same-
  // grade colors share). The box must be an actual stem-fill box (a populated
  // ladder - NOT a discrete whole-box hydrangea, which is box-mode/immutable)
  // and not full. Same-grade => same capacity, so any same-grade color fits
  // until the box is full.
  if (saleModeOf(candidate) === "box_stem") {
    const boxV = resolve(first.slug, lookup) ?? staticVarietyBySlug(first.slug);
    if (!boxV || saleModeOf(boxV) !== "box_stem") return false;
    if ((boxV.grade ?? null) !== (candidate.grade ?? null)) return false;
    const stemFill = computeBoxFill(box, lookup);
    if (stemFill.mode !== "stems" || stemFill.ladder.length === 0) return false;
    return !stemFill.full;
  }

  const spb = candidate.stemsPerBunch;
  if (spb === null || spb <= 0) return false;
  const fill = computeBoxFill(box, lookup);
  if (fill.full) return false;
  const len =
    candidate.lengths.length > 0
      ? (first.length ?? candidate.lengths[0])
      : undefined;
  const denom = unitsPerBoxAtSize(candidate, len, box.size);
  if (!denom || denom <= 0) return false;
  return fill.totalShare + 1 / denom <= 1 + EPS;
}
